import React, { useEffect, useState } from 'react'

import { AppErrorDispatchType, useAppErrorDispatch } from '@contexts/AppError'
import {
  listenHubEvents,
  onLoad,
  TAuthState,
} from './helpers'
import { useOktaAuth } from '@okta/okta-react'
import { AUTH_STATE } from '@enums/auth-state'

import { identify } from '@api/segment'

const AuthStateContext = React.createContext<TAuthState>(null)

/**
 * calls to Amplify.Auth.<signUp | signUn | signOut> are captured by `hubListener`,
 * which in turn sets the React Context Auth State state, and propagates state to
 * child Components who are using AuthStateContext.
 *
 * @returns Context Component to provide the auth state value to children
 */
export const AuthProvider: React.FC = ({ children }) => {
  const [appAuthState, setAuthState] = useState<TAuthState>(null)
  const { authState, oktaAuth } = useOktaAuth()
  const appErrorDispatch = useAppErrorDispatch() as AppErrorDispatchType

  useEffect(() => {
    listenHubEvents(setAuthState, appErrorDispatch)
  }, [appErrorDispatch])

  useEffect(() => {
    // do an initial query of the auth state — check if the user is already auth'd
    onLoad(setAuthState, appErrorDispatch, oktaAuth)
  }, [appErrorDispatch, oktaAuth, authState])


  useEffect(() => {
    if(appAuthState === AUTH_STATE.UNAUTHD) {
      identify()
    }
  }, [appAuthState])

  return (
    <AuthStateContext.Provider value={appAuthState}>
      {children}
    </AuthStateContext.Provider>
  )
}

/**
 * provide the current auth state to Components using the context
 */
export function useAuthState() {
  const context = React.useContext(AuthStateContext)
  return context
}
